import type React from 'react';
import { useMemo, useState } from 'react';
import { ChevronRight, Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MovementType, UnifiedMovement } from '@/types/manager.model';
import { currencyFormatter, formatMovementDate } from '@/utils/movements-utils';
import {
  SettlementBadge,
  TransferStatusBadge,
  TypeChip,
} from './movement-chips';

export type TypeFilter = MovementType | 'all';

interface MovementsTableProps {
  movements: UnifiedMovement[];
  typeFilter: TypeFilter;
  onTypeFilterChange: (filter: TypeFilter) => void;
  onSelectTransfer: (requestId: string) => void;
}

const FILTERS: { value: TypeFilter; label: string; shortLabel: string }[] = [
  { value: 'all', label: 'Todos los movimientos', shortLabel: 'Todos' },
  { value: 'torneo', label: 'Torneos', shortLabel: 'Torneos' },
  { value: 'clase', label: 'Clases', shortLabel: 'Clases' },
  { value: 'partido', label: 'Partidos', shortLabel: 'Partidos' },
  { value: 'transferencia', label: 'Transferencias', shortLabel: 'Transferencias' },
];

const SearchInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => (
  <div className="relative flex-1">
    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9CA3AF]" />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-10 w-full rounded-lg border border-[#E7E9EE] bg-white pl-9 pr-9 text-sm outline-none focus:border-[#2E66E5]"
    />
    {value && (
      <button
        type="button"
        onClick={() => onChange('')}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[#9CA3AF] hover:text-[#374151]"
        aria-label="Limpiar"
      >
        <X className="h-4 w-4" />
      </button>
    )}
  </div>
);

const Amount: React.FC<{ value: number; className?: string }> = ({
  value,
  className,
}) => (
  <span
    className={cn(
      'font-semibold',
      value < 0 ? 'text-[#DC2626]' : 'text-[#16A34A]',
      className,
    )}
  >
    {currencyFormatter.format(value)}
  </span>
);

const Concepto: React.FC<{ movement: UnifiedMovement }> = ({ movement }) => (
  <div className="flex flex-col items-start gap-1">
    <TypeChip tipo={movement.tipo} />
    <span className="font-medium text-[#1F2937]">{movement.concepto}</span>
  </div>
);

const MovementsTable: React.FC<MovementsTableProps> = ({
  movements,
  typeFilter,
  onTypeFilterChange,
  onSelectTransfer,
}) => {
  const [emailQuery, setEmailQuery] = useState('');
  const [conceptQuery, setConceptQuery] = useState('');

  const counts = useMemo(() => {
    const base: Record<TypeFilter, number> = {
      all: movements.length,
      torneo: 0,
      clase: 0,
      partido: 0,
      transferencia: 0,
    };
    movements.forEach((m) => {
      base[m.tipo] += 1;
    });
    return base;
  }, [movements]);

  const filtered = useMemo(() => {
    const email = emailQuery.trim().toLowerCase();
    const concept = conceptQuery.trim().toLowerCase();
    return movements.filter((m) => {
      if (typeFilter !== 'all' && m.tipo !== typeFilter) return false;
      if (email && !m.cliente?.toLowerCase().includes(email)) return false;
      if (concept) {
        const haystack = `${m.concepto} ${m.conceptoSub ?? ''}`.toLowerCase();
        if (!haystack.includes(concept)) return false;
      }
      return true;
    });
  }, [movements, typeFilter, emailQuery, conceptQuery]);

  const handleRowClick = (movement: UnifiedMovement) => {
    if (movement.tipo === 'transferencia' && movement.requestId) {
      onSelectTransfer(movement.requestId);
    }
  };

  return (
    <div className="mt-8">
      <h2 className="mb-4 text-2xl font-bold tracking-[-0.3px] text-[#1A2466]">
        Historial de Movimientos
      </h2>

      {/* Filtros: chips */}
      <div className="-mx-1 mb-3 flex gap-2 overflow-x-auto px-1 pb-1">
        {FILTERS.map((filter) => {
          const active = typeFilter === filter.value;
          return (
            <button
              key={filter.value}
              type="button"
              onClick={() => onTypeFilterChange(filter.value)}
              className={cn(
                'flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] transition-colors',
                active
                  ? 'border-[#1F2937] bg-[#1F2937] text-white'
                  : 'border-[#E7E9EE] bg-white text-[#374151] hover:bg-[#F8F9FB]',
              )}
            >
              <span className="sm:hidden">{filter.shortLabel}</span>
              <span className="hidden sm:inline">{filter.label}</span>
              <span
                className={cn(
                  'rounded-full px-1.5 py-px text-[11px] font-semibold',
                  active ? 'bg-white/20 text-white' : 'bg-[#F1F2F5] text-[#374151]',
                )}
              >
                {counts[filter.value]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Búsqueda */}
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <SearchInput
          value={emailQuery}
          onChange={setEmailQuery}
          placeholder="Buscar por email"
        />
        <SearchInput
          value={conceptQuery}
          onChange={setConceptQuery}
          placeholder="Buscar por concepto"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-md border border-[#E7E9EE] p-4 text-sm text-[#6B7280]">
          No hay movimientos para mostrar.
        </div>
      ) : (
        <>
          {/* Desktop: table */}
          <div className="hidden w-full max-w-full overflow-x-auto rounded-xl border border-[#E7E9EE] md:block">
            <table className="w-full min-w-[920px] table-auto text-left text-sm">
              <thead className="bg-[#F8F9FB]">
                <tr className="text-[12.5px] uppercase tracking-[0.4px] text-[#6B7280]">
                  <th className="px-3 py-3 font-semibold">Fecha y hora</th>
                  <th className="px-3 py-3 font-semibold">Monto</th>
                  <th className="px-3 py-3 font-semibold">Saldo posterior</th>
                  <th className="px-3 py-3 font-semibold">Cliente</th>
                  <th className="px-3 py-3 font-semibold">Concepto</th>
                  <th className="px-3 py-3 font-semibold">Modalidad</th>
                  <th className="px-3 py-3 font-semibold">Método de pago</th>
                  <th className="px-3 py-3 font-semibold">Valor</th>
                  <th className="px-3 py-3 font-semibold">Estado</th>
                  <th className="w-8 px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const clickable = m.tipo === 'transferencia' && !!m.requestId;
                  return (
                    <tr
                      key={m.id}
                      onClick={() => handleRowClick(m)}
                      className={cn(
                        'border-t border-[#EEF0F4]',
                        clickable
                          ? 'cursor-pointer hover:bg-[#F8F9FB]'
                          : 'hover:bg-[#F8F9FB]/60',
                      )}
                    >
                      <td className="whitespace-nowrap px-3 py-3 text-[#374151]">
                        {formatMovementDate(m.date)}
                      </td>
                      <td className="px-3 py-3">
                        <Amount value={m.amount} />
                      </td>
                      <td className="px-3 py-3 text-[#374151]">
                        {m.saldoPosterior != null
                          ? currencyFormatter.format(m.saldoPosterior)
                          : '—'}
                      </td>
                      <td className="px-3 py-3 text-[#374151]">
                        {m.cliente ?? '—'}
                      </td>
                      <td className="px-3 py-3">
                        <Concepto movement={m} />
                      </td>
                      <td className="px-3 py-3 text-[#374151]">
                        {m.modalidad ?? '—'}
                      </td>
                      <td className="px-3 py-3 text-[#374151]">
                        {m.metodo ?? '—'}
                      </td>
                      <td className="px-3 py-3 text-[#374151]">
                        {m.valor != null ? currencyFormatter.format(m.valor) : '—'}
                      </td>
                      <td className="px-3 py-3">
                        {m.status ? (
                          <TransferStatusBadge status={m.status} />
                        ) : m.settlement ? (
                          <SettlementBadge settlement={m.settlement} />
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="w-8 px-3 py-3 text-[#9CA3AF]">
                        {clickable && <ChevronRight className="h-4 w-4" />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile: cards */}
          <div className="flex flex-col gap-3 md:hidden">
            {filtered.map((m) => {
              const clickable = m.tipo === 'transferencia' && !!m.requestId;
              return (
                <div
                  key={m.id}
                  onClick={() => handleRowClick(m)}
                  className={cn(
                    'rounded-xl border border-[#E7E9EE] p-3.5',
                    clickable && 'cursor-pointer border-l-2 border-l-[#2E66E5]',
                  )}
                >
                  <div className="mb-2 flex items-start justify-between gap-2">
                    <div className="flex flex-col gap-1">
                      <TypeChip tipo={m.tipo} />
                      <span className="text-xs text-[#6B7280]">
                        {formatMovementDate(m.date)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {m.status ? (
                        <TransferStatusBadge status={m.status} />
                      ) : (
                        m.settlement && <SettlementBadge settlement={m.settlement} />
                      )}
                      {clickable && (
                        <ChevronRight className="h-4 w-4 shrink-0 text-[#9CA3AF]" />
                      )}
                    </div>
                  </div>

                  <p className="text-[15px] font-semibold text-[#1F2937]">
                    {m.concepto}
                  </p>
                  {m.conceptoSub && (
                    <p className="text-[12.5px] text-[#6B7280]">{m.conceptoSub}</p>
                  )}

                  <div className="my-3 border-t border-dashed border-[#E7E9EE]" />

                  <div className="flex items-end justify-between">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.4px] text-[#9CA3AF]">
                        Monto
                      </p>
                      <Amount value={m.amount} className="text-lg" />
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.4px] text-[#9CA3AF]">
                        Saldo posterior
                      </p>
                      <p className="text-sm font-medium text-[#374151]">
                        {m.saldoPosterior != null
                          ? currencyFormatter.format(m.saldoPosterior)
                          : '—'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-[13px]">
                    <CardMeta label="Cliente" value={m.cliente ?? '—'} />
                    <CardMeta label="Modalidad" value={m.modalidad ?? '—'} />
                    <CardMeta label="Método" value={m.metodo ?? '—'} />
                    <CardMeta
                      label="Valor"
                      value={
                        m.valor != null ? currencyFormatter.format(m.valor) : '—'
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const CardMeta: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div>
    <p className="text-[11px] uppercase tracking-[0.4px] text-[#9CA3AF]">
      {label}
    </p>
    <p className="truncate text-[#374151]">{value}</p>
  </div>
);

export default MovementsTable;
